import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import {
  createColumn,
  getRegister,
  formatDateTime,
  calculateDuration,
  createViewAction,
} from "../../utils/helpers";
import { URL_CURRENTLY_PARKED_CARS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_CURRENTLY_ACTIVE_PARKINGS, API_SLOTS } from "../../utils/apis";

// Helper function to resolve slot name by ID
const resolveSlotName = (slotId: any, slotsData: any) => {
  if (!slotId || !slotsData) {
    return "Unknown Slot";
  }

  // Handle both array and object with results property
  const slotsArray = Array.isArray(slotsData)
    ? slotsData
    : slotsData?.results || [];

  if (!Array.isArray(slotsArray) || slotsArray.length === 0) {
    return "Loading...";
  }

  const slot = slotsArray.find((s) => s.id === slotId);
  return slot ? slot.name : "Unknown Slot";
};

export const useCurrentlyActiveParkedCars = () => {
  // The register layout already fetches API_CURRENTLY_ACTIVE_PARKINGS with the
  // active filters, so this hook must not fetch it again. Each row already
  // carries `parking_name`, which is what the table column renders.

  // Get slots data
  const {
    loadData: loadSlotsData,
    loading: loadingSlots,
    data: slotsData,
  } = useGet();

  useEffect(() => {
    loadSlotsData({ api: API_SLOTS });
  }, [loadSlotsData]);

  const tableColumns = () => [
    createColumn("car_number", true, "Number Plate"),
    createColumn("phone_number", true),
    createColumn("parking_name", false),
    createColumn("slot_name", false),
    createColumn("start_time", { type: "date" }),
    createColumn("end_time", { type: "date" }),
    createColumn("duration", false),
    createColumn("status", false),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      // Check if parking is actually ended and has an end_time
      const isActuallyEnded = item.is_parking_ended && item.end_time;

      const duration = isActuallyEnded
        ? calculateDuration(item.start_time, item.end_time)
        : calculateDuration(item.start_time, new Date().toISOString());

      return {
        ...item,
        start_time: formatDateTime(item.start_time),
        end_time: isActuallyEnded ? formatDateTime(item.end_time) : "Active",
        duration: duration,
        status: isActuallyEnded ? "Ended" : "Active",
        parking_name: item?.parking_name,
        slot_name: item?.slot_name,
        actions: (
          <FormActions
            actions={[createViewAction(URL_CURRENTLY_PARKED_CARS, item?.id)]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    // Check if parking is actually ended and has an end_time
    const isActuallyEnded = item.is_parking_ended && item.end_time;

    const duration = isActuallyEnded
      ? calculateDuration(item.start_time, item.end_time)
      : item.start_time
        ? calculateDuration(item.start_time, new Date().toISOString())
        : "N/A";

    return {
      sections: [
        {
          title: "Vehicle Information",
          fields: {
            car_number: item?.car_number || "N/A",
            phone_number: item?.phone_number || "N/A",
            email: item?.email || "Not provided",
            car_size: item?.car_size || "Not specified",
          },
        },
        {
          title: "Parking Details",
          fields: {
            parking_location: item?.parking_name || "Unknown Parking",
            parking_slot: resolveSlotName(item?.slot, slotsData),
            status: isActuallyEnded ? "Ended" : "Active",
            declaration: item?.declaration ? "Accepted" : "Not accepted",
          },
        },
        {
          title: "Time Information",
          fields: {
            start_time: item?.start_time
              ? formatDateTime(item.start_time)
              : "N/A",
            end_time: isActuallyEnded
              ? formatDateTime(item.end_time)
              : "Still active",
            duration: duration,
            parking_status: item?.is_parking_ended
              ? "Marked as ended"
              : "Active session",
          },
        },
        {
          title: "Location Coordinates",
          fields: {
            latitude: item?.latitude || "0",
            longitude: item?.longitude || "0",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
          },
        },
      ],
    };
  };
  const activityFilter = "parked_cars";

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
    slotsData,
    loadingSlots,
  };
};

export const useCurrentlyActiveParkedCarsForm = () => {
  const {
    loadData: loadCurrentlyActiveParkings,
    loading: loadingParkings,
    data: parkings,
  } = useGet();
  const { loadData: loadSlots, loading: loadingSlots, data: slots } = useGet();

  useEffect(() => {
    loadCurrentlyActiveParkings({ api: API_CURRENTLY_ACTIVE_PARKINGS });
    loadSlots({ api: API_SLOTS });
  }, []);

  const fields: any = [
    {
      field_name: "car_number",
      label: "Car Number Plate",
      type: "text",
      props: { required: true, placeholder: "e.g., UAG 622P" },
    },
    {
      field_name: "phone_number",
      label: "Phone Number",
      type: "tel",
      props: { required: true, placeholder: "e.g., 704435121" },
    },
    {
      field_name: "email",
      label: "Email Address",
      type: "email",
      props: { required: false, placeholder: "example@email.com" },
    },
    {
      field_name: "parking",
      label: "Parking Location",
      type: "select",
      props: { required: true },
      options_data: parkings,
      loading: loadingParkings,
    },
    {
      field_name: "slot",
      label: "Parking Slot",
      type: "select",
      props: { required: true },
      options_data: slots,
      loading: loadingSlots,
    },
    {
      field_name: "latitude",
      label: "Latitude",
      type: "number",
      props: { required: false, step: "any", placeholder: "0.0" },
    },
    {
      field_name: "longitude",
      label: "Longitude",
      type: "number",
      props: { required: false, step: "any", placeholder: "0.0" },
    },
    {
      field_name: "declaration",
      label: "Declaration Accepted",
      type: "checkbox",
      props: { required: false },
    },
  ];

  const sections: any = [
    {
      title: "Vehicle Information",
      fields: fields.slice(0, 3), // car_number, phone_number, email
    },
    {
      title: "Parking Details",
      fields: fields.slice(3, 5), // parking, slot
    },
    {
      title: "Location & Additional Info",
      fields: fields.slice(5), // latitude, longitude, declaration
    },
  ];

  const redirect = getRegister(URL_CURRENTLY_PARKED_CARS);

  const defaultValues = {
    latitude: "0",
    longitude: "0",
    declaration: false,
    is_parking_ended: false,
    deleted: false,
  };

  return { fields, sections, redirect, defaultValues };
};

export const CurrentlyActiveParkedCarsForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { fields } = useCurrentlyActiveParkedCarsForm();
  return (
    <FormFields
      fields={fields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};
